import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import MuiButton from '@mui/material/Button';
import LandingLayout from './components/LandingLayout';
import { track, Events } from 'src/utils/analytics';

// ─── Types ────────────────────────────────────────────────────────────────────

interface WindowState {
  id: string;
  x: number;
  y: number;
  open: boolean;
  zIndex: number;
}

// ─── Draggable Window Shell ───────────────────────────────────────────────────

interface NotebookWindowProps {
  id: string;
  title: string;
  emoji: string;
  accentColor: string;
  state: WindowState;
  onClose: (id: string) => void;
  onFocus: (id: string) => void;
  onMove: (id: string, x: number, y: number) => void;
  children: React.ReactNode;
  width?: number;
}

function NotebookWindow({ id, title, emoji, accentColor, state, onClose, onFocus, onMove, children, width = 340 }: NotebookWindowProps) {
  const dragRef = useRef<{ startX: number; startY: number; winX: number; winY: number } | null>(null);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    onFocus(id);
    dragRef.current = { startX: e.clientX, startY: e.clientY, winX: state.x, winY: state.y };

    const handleDrag = (me: MouseEvent) => {
      if (!dragRef.current) return;
      const dx = me.clientX - dragRef.current.startX;
      const dy = me.clientY - dragRef.current.startY;
      onMove(id, dragRef.current.winX + dx, dragRef.current.winY + dy);
    };
    const onUp = () => {
      dragRef.current = null;
      window.removeEventListener('mousemove', handleDrag);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', handleDrag);
    window.addEventListener('mouseup', onUp);
  }, [id, state.x, state.y, onFocus, onMove]);

  if (!state.open) return null;

  return (
    <Box
      onClick={() => onFocus(id)}
      sx={{
        position: 'absolute',
        left: state.x,
        top: state.y,
        width,
        zIndex: state.zIndex,
        borderRadius: '10px',
        border: '2px solid #1e1e1e',
        boxShadow: '6px 6px 0px #1e1e1e',
        backgroundColor: '#FFFFFF',
        overflow: 'hidden',
        userSelect: 'none',
        transition: 'box-shadow 80ms ease',
      }}
    >
      {/* Title bar */}
      <Box
        onMouseDown={handleMouseDown}
        sx={{
          backgroundColor: accentColor,
          borderBottom: '2px solid #1e1e1e',
          px: 2,
          py: 1.25,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: 'grab',
          '&:active': { cursor: 'grabbing' },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography sx={{ fontSize: '1rem' }}>{emoji}</Typography>
          <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 700, fontSize: '0.8rem', color: '#1e1e1e' }}>
            {title}
          </Typography>
        </Box>
        {/* Traffic light buttons */}
        <Box sx={{ display: 'flex', gap: 0.75 }}>
          <Box
            onClick={(e) => { e.stopPropagation(); onClose(id); }}
            sx={{
              width: 12, height: 12, borderRadius: '50%',
              backgroundColor: '#FF5F57', border: '1.5px solid #E0443E',
              cursor: 'pointer', '&:hover': { opacity: 0.8 },
            }}
          />
          <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: '#FEBC2E', border: '1.5px solid #D4A017' }} />
          <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: '#28C840', border: '1.5px solid #1DAD2B' }} />
        </Box>
      </Box>

      {/* Content */}
      <Box sx={{ p: 2.5 }}>
        {children}
      </Box>
    </Box>
  );
}

// ─── Window Contents ──────────────────────────────────────────────────────────

function NewspaperContent() {
  return (
    <Box>
      <Typography sx={{ fontFamily: 'Bitter, Georgia, serif', fontSize: '1.2rem', fontWeight: 700, textAlign: 'center', borderBottom: '3px double #1a1a1a', pb: 1, mb: 1 }}>
        The Classroom Times
      </Typography>
      <Typography sx={{ fontFamily: 'Bitter, Georgia, serif', fontSize: '0.55rem', textAlign: 'center', textTransform: 'uppercase', letterSpacing: '0.15em', color: '#888', mb: 2 }}>
        Spring Edition &bull; Room 214 &bull; Written by Students
      </Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
        {[
          { emoji: '🌱', title: 'School Garden Opens Its Doors', tag: 'News', featured: true },
          { emoji: '💬', title: 'Why Recess Should Be Longer', tag: 'Opinion' },
          { emoji: '📚', title: 'Dog Man: A Review', tag: 'Review' },
          { emoji: '🍕', title: 'Cafeteria Gets a Makeover', tag: 'News' },
        ].map((s) => (
          <Box key={s.title} sx={{ p: 1.25, backgroundColor: s.featured ? '#FFF9C4' : '#F7F7F7', border: '1.5px solid #1e1e1e', borderRadius: '4px', boxShadow: '2px 2px 0 #1e1e1e' }}>
            <Typography sx={{ fontSize: '1rem', mb: 0.5 }}>{s.emoji}</Typography>
            <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontSize: '0.7rem', fontWeight: 700, lineHeight: 1.3, mb: 0.5 }}>{s.title}</Typography>
            <Box sx={{ display: 'inline-block', backgroundColor: '#2C2C2C', color: '#fff', fontSize: '0.5rem', fontFamily: 'Montserrat, sans-serif', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', px: 0.75, py: 0.25, borderRadius: '2px' }}>{s.tag}</Box>
          </Box>
        ))}
      </Box>
      <Typography sx={{ fontFamily: 'Lato, sans-serif', fontSize: '0.7rem', color: '#888', textAlign: 'center', mt: 2 }}>
        Published by Ms. Rivera&apos;s 4th Grade &bull; May 2026
      </Typography>
    </Box>
  );
}

function WritingScaffoldContent() {
  const [active, setActive] = useState(0);
  const blocks = [
    { label: 'Hook', color: '#E3F2FD', starter: 'Have you ever wondered why the cafeteria pizza always tastes...', filled: 'Have you ever wondered why the cafeteria pizza always tastes different on Fridays? I have a theory.' },
    { label: 'Claim', color: '#F3E5F5', starter: 'I believe that...', filled: 'I believe that the school should switch to a rotating menu every week.' },
    { label: 'Reason 1', color: '#E8F5E9', starter: 'One reason is...', filled: 'One reason is that students get bored eating the same things.' },
    { label: 'Reason 2', color: '#FFF3E0', starter: 'Another reason is...', filled: '' },
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 0.5, mb: 2, flexWrap: 'wrap' }}>
        {['📝 Opinion', '📰 News Story', '📖 Review'].map((t, i) => (
          <Box key={t} sx={{ fontSize: '0.6rem', fontFamily: 'Montserrat, sans-serif', fontWeight: 700, px: 1, py: 0.4, border: '1.5px solid #1e1e1e', borderRadius: '4px', backgroundColor: i === 0 ? '#FFE082' : '#fff', boxShadow: i === 0 ? '1px 1px 0 #1e1e1e' : 'none', cursor: 'pointer' }}>{t}</Box>
        ))}
      </Box>
      <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 800, fontSize: '0.85rem', mb: 2, color: '#2C2C2C' }}>
        My Opinion on School Lunch
      </Typography>
      {blocks.map((block, i) => (
        <Box
          key={block.label}
          onClick={() => setActive(i)}
          sx={{ mb: 1, p: 1.25, backgroundColor: i === active ? block.color : '#F7F7F7', borderRadius: '4px', border: `1.5px solid ${i === active ? '#1e1e1e' : '#ddd'}`, cursor: 'pointer', transition: 'all 100ms' }}
        >
          <Typography sx={{ fontSize: '0.5rem', fontWeight: 700, fontFamily: 'Montserrat, sans-serif', color: '#888', textTransform: 'uppercase', letterSpacing: '0.08em', mb: 0.4 }}>
            {block.label}
          </Typography>
          <Typography sx={{ fontSize: '0.7rem', fontFamily: 'Lato, sans-serif', color: block.filled ? '#2C2C2C' : '#aaa', fontStyle: block.filled ? 'normal' : 'italic', lineHeight: 1.5 }}>
            {block.filled || block.starter}
          </Typography>
        </Box>
      ))}
      <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box sx={{ height: 4, flex: 1, backgroundColor: '#E0E0E0', borderRadius: 2 }}>
          <Box sx={{ height: 4, width: '65%', backgroundColor: '#FFB300', borderRadius: 2 }} />
        </Box>
        <Typography sx={{ fontSize: '0.6rem', fontFamily: 'Montserrat, sans-serif', fontWeight: 700, color: '#888' }}>65%</Typography>
      </Box>
    </Box>
  );
}

function AlignContent() {
  const [input, setInput] = useState('CCSS.ELA-LITERACY.W.4.1');
  const [generated, setGenerated] = useState(true);

  return (
    <Box>
      <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 700, fontSize: '0.75rem', color: '#555', mb: 1 }}>
        Paste a standard or topic:
      </Typography>
      <Box
        component="input"
        value={input}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => { setInput(e.target.value); setGenerated(false); }}
        sx={{
          width: '100%', fontFamily: 'Lato, sans-serif', fontSize: '0.8rem', p: 1, border: '2px solid #1e1e1e', borderRadius: '4px', backgroundColor: '#F7F7F7', mb: 1.5, boxSizing: 'border-box', outline: 'none',
          '&:focus': { backgroundColor: '#FFF', borderColor: '#FFB300' },
        }}
      />
      {generated && (
        <Box sx={{ backgroundColor: '#F0FFF4', border: '1.5px solid #1e1e1e', borderRadius: '6px', p: 2, boxShadow: '2px 2px 0 #1e1e1e' }}>
          <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 700, fontSize: '0.65rem', color: '#888', textTransform: 'uppercase', letterSpacing: '0.08em', mb: 1 }}>
            ✨ Generated prompt
          </Typography>
          <Typography sx={{ fontFamily: 'Lato, sans-serif', fontSize: '0.8rem', color: '#2C2C2C', lineHeight: 1.6, mb: 1.5 }}>
            <strong>Write an opinion piece</strong> about a rule or policy at your school you would change. Use evidence from your own experience to support your argument.
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {['Opinion', 'Grade 4', '45 min', 'Sentence starters included'].map((tag) => (
              <Box key={tag} sx={{ fontSize: '0.55rem', fontFamily: 'Montserrat, sans-serif', fontWeight: 700, px: 0.75, py: 0.3, backgroundColor: '#2C2C2C', color: '#fff', borderRadius: '3px' }}>{tag}</Box>
            ))}
          </Box>
        </Box>
      )}
      {!generated && (
        <Box
          onClick={() => setGenerated(true)}
          sx={{ mt: 0.5, p: 1, backgroundColor: '#2C2C2C', color: '#fff', borderRadius: '4px', border: '2px solid #1e1e1e', boxShadow: '2px 2px 0 #1e1e1e', textAlign: 'center', cursor: 'pointer', fontFamily: 'Montserrat, sans-serif', fontWeight: 700, fontSize: '0.75rem', '&:hover': { transform: 'translate(1px,1px)', boxShadow: '1px 1px 0 #1e1e1e' }, transition: 'all 80ms' }}
        >
          ✨ Generate prompt
        </Box>
      )}
    </Box>
  );
}

function RGRContent() {
  const lessons = [
    { unit: 'Unit 2', title: 'The Great Kapok Tree', type: 'Opinion', grade: '3–5', standard: 'W.3.1' },
    { unit: 'Unit 4', title: 'Esperanza Rising', type: 'Personal Narrative', grade: '4–6', standard: 'W.4.3' },
    { unit: 'Unit 6', title: 'Seedfolks', type: 'News Story', grade: '5–7', standard: 'W.5.2' },
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#4CAF50' }} />
        <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontSize: '0.7rem', fontWeight: 700, color: '#2C2C2C' }}>
          Reading &amp; Writing Workshop — aligned lessons
        </Typography>
      </Box>
      {lessons.map((l) => (
        <Box key={l.unit} sx={{ mb: 1.5, p: 1.5, border: '1.5px solid #1e1e1e', borderRadius: '6px', backgroundColor: '#FAFAFA', boxShadow: '2px 2px 0 #1e1e1e', cursor: 'pointer', transition: 'all 80ms', '&:hover': { transform: 'translate(1px,1px)', boxShadow: '1px 1px 0 #1e1e1e', backgroundColor: '#FFF9C4' } }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
            <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 800, fontSize: '0.8rem', color: '#2C2C2C' }}>{l.title}</Typography>
            <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontSize: '0.6rem', fontWeight: 700, color: '#888' }}>{l.unit}</Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
            {[l.type, `Grade ${l.grade}`, l.standard].map((tag) => (
              <Box key={tag} sx={{ fontSize: '0.55rem', fontFamily: 'Montserrat, sans-serif', fontWeight: 700, px: 0.75, py: 0.25, backgroundColor: '#E3F2FD', border: '1px solid #1e1e1e', borderRadius: '3px', color: '#1e1e1e' }}>{tag}</Box>
            ))}
          </Box>
        </Box>
      ))}
      <Typography sx={{ fontFamily: 'Lato, sans-serif', fontSize: '0.65rem', color: '#888', textAlign: 'center', mt: 1 }}>
        +47 more aligned lessons in the gallery
      </Typography>
    </Box>
  );
}

// ─── Dock ─────────────────────────────────────────────────────────────────────

interface DockProps {
  windows: { id: string; emoji: string; title: string; open: boolean }[];
  onOpen: (id: string) => void;
}

function Dock({ windows, onOpen }: DockProps) {
  const closed = windows.filter((w) => !w.open);
  if (closed.length === 0) return null;

  return (
    <Box
      sx={{
        position: 'absolute',
        bottom: 24,
        left: '50%',
        transform: 'translateX(-50%)',
        display: 'flex',
        gap: 1.5,
        backgroundColor: 'rgba(255,255,255,0.85)',
        backdropFilter: 'blur(8px)',
        border: '2px solid #1e1e1e',
        borderRadius: '12px',
        px: 2,
        py: 1,
        boxShadow: '4px 4px 0 #1e1e1e',
        zIndex: 100,
      }}
    >
      {closed.map((w) => (
        <Box
          key={w.id}
          onClick={() => onOpen(w.id)}
          title={w.title}
          sx={{
            width: 40, height: 40, borderRadius: '8px', border: '1.5px solid #1e1e1e',
            backgroundColor: '#F7F7F7', display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '1.2rem', cursor: 'pointer', boxShadow: '2px 2px 0 #1e1e1e',
            transition: 'all 80ms', '&:hover': { transform: 'translateY(-4px)', boxShadow: '2px 6px 0 #1e1e1e' },
          }}
        >
          {w.emoji}
        </Box>
      ))}
    </Box>
  );
}

// ─── Desktop ──────────────────────────────────────────────────────────────────

const INITIAL_WINDOWS: WindowState[] = [
  { id: 'newspaper', x: 40,  y: 60,  open: true, zIndex: 1 },
  { id: 'writing',   x: 400, y: 30,  open: true, zIndex: 2 },
  { id: 'align',     x: 80,  y: 360, open: true, zIndex: 3 },
  { id: 'rgr',       x: 460, y: 320, open: true, zIndex: 4 },
];

const WINDOW_DEFS = [
  { id: 'newspaper', title: 'The Classroom Times', emoji: '🗞️', accentColor: '#FFF9C4', width: 340 },
  { id: 'writing',   title: 'Student draft — Opinion',  emoji: '✏️', accentColor: '#E3F2FD', width: 320 },
  { id: 'align',     title: 'Align a lesson',  emoji: '📋', accentColor: '#F3E5F5', width: 320 },
  { id: 'rgr',       title: 'RGR — aligned lessons',    emoji: '📖', accentColor: '#E8F5E9', width: 300 },
];

function Desktop() {
  const [wins, setWins] = useState(INITIAL_WINDOWS);
  const maxZ = useRef(10);

  const handleClose = useCallback((id: string) => {
    setWins((prev) => prev.map((w) => w.id === id ? { ...w, open: false } : w));
  }, []);

  const handleOpen = useCallback((id: string) => {
    maxZ.current += 1;
    setWins((prev) => prev.map((w) => w.id === id ? { ...w, open: true, zIndex: maxZ.current } : w));
  }, []);

  const handleFocus = useCallback((id: string) => {
    maxZ.current += 1;
    setWins((prev) => prev.map((w) => w.id === id ? { ...w, zIndex: maxZ.current } : w));
  }, []);

  const handleMove = useCallback((id: string, x: number, y: number) => {
    setWins((prev) => prev.map((w) => w.id === id ? { ...w, x, y } : w));
  }, []);

  const dockItems = WINDOW_DEFS.map((d) => ({ ...d, open: wins.find((w) => w.id === d.id)?.open ?? true }));

  return (
    <Box sx={{ position: 'relative', width: '100%', height: '100%', minHeight: 660 }}>
      {WINDOW_DEFS.map((def) => {
        const state = wins.find((w) => w.id === def.id)!;
        return (
          <NotebookWindow
            key={def.id}
            id={def.id}
            title={def.title}
            emoji={def.emoji}
            accentColor={def.accentColor}
            state={state}
            onClose={handleClose}
            onFocus={handleFocus}
            onMove={handleMove}
            width={def.width}
          >
            {def.id === 'newspaper' && <NewspaperContent />}
            {def.id === 'writing'   && <WritingScaffoldContent />}
            {def.id === 'align'     && <AlignContent />}
            {def.id === 'rgr'       && <RGRContent />}
          </NotebookWindow>
        );
      })}
      <Dock windows={dockItems} onOpen={handleOpen} />
    </Box>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function LandingPageV3() {
  const navigate = useNavigate();
  const [mounted, setMounted] = useState(false);
  useEffect(() => { setMounted(true); }, []);

  return (
    <LandingLayout>
      {/* Hero */}
      <Box
        sx={{
          backgroundColor: '#FFFFFF',
          borderBottom: '2px solid #1e1e1e',
          overflow: 'hidden',
          minHeight: { xs: 'auto', md: 760 },
          position: 'relative',
        }}
      >
        <Box sx={{ maxWidth: 1200, mx: 'auto', px: { xs: 3, md: 6 }, pt: { xs: 6, md: 10 }, pb: { xs: 4, md: 6 }, display: 'grid', gridTemplateColumns: { xs: '1fr', md: '440px 1fr' }, gap: { xs: 6, md: 6 }, alignItems: 'flex-start' }}>

          {/* Left: copy */}
          <Box sx={{ position: 'relative', zIndex: 10 }}>
            <Box sx={{ display: 'inline-block', backgroundColor: '#FFF9C4', border: '2px solid #1e1e1e', borderRadius: '4px', px: 1.5, py: 0.5, mb: 3, boxShadow: '2px 2px 0 #1e1e1e' }}>
              <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                The K–12 writing platform
              </Typography>
            </Box>

            <Typography
              variant="h1"
              sx={{
                fontFamily: 'Montserrat, sans-serif',
                fontSize: { xs: '2.4rem', md: '3.25rem' },
                fontWeight: 800,
                lineHeight: 1.1,
                mb: 3,
                color: '#2C2C2C',
                letterSpacing: '-0.02em',
              }}
            >
              Writing that{' '}
              <Box component="span" sx={{ textDecoration: 'underline', textDecorationColor: '#FFB300', textUnderlineOffset: '6px', textDecorationThickness: '4px' }}>
                goes somewhere
              </Box>
            </Typography>

            <Typography sx={{ fontFamily: 'Lato, sans-serif', fontSize: '1.1rem', lineHeight: 1.75, color: '#555', mb: 4, maxWidth: 400 }}>
              Scaffold any lesson into structured writing. Align to your curriculum. Publish to a real classroom newspaper. One platform, every use case.
            </Typography>

            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
              <MuiButton
                variant="contained"
                onClick={() => { track(Events.ClickedCTA, { location: 'hero_v3', label: 'Try it free', destination: '/register' }); navigate('/register'); }}
                sx={{ backgroundColor: '#2C2C2C', color: '#fff', fontFamily: 'Montserrat, sans-serif', fontWeight: 700, fontSize: '1rem', textTransform: 'none', borderRadius: '6px', border: '2px solid #1e1e1e', boxShadow: '4px 4px 0 #1e1e1e', px: 4, py: 1.5, '&:hover': { backgroundColor: '#1e1e1e', boxShadow: '2px 2px 0 #1e1e1e', transform: 'translate(2px,2px)' }, transition: 'all 100ms' }}
              >
                Try it free →
              </MuiButton>
              <MuiButton
                variant="outlined"
                onClick={() => { track(Events.ClickedCTA, { location: 'hero_v3', label: 'See it for districts', destination: '/contact' }); navigate('/contact'); }}
                sx={{ borderColor: '#2C2C2C', border: '2px solid #2C2C2C', color: '#2C2C2C', fontFamily: 'Montserrat, sans-serif', fontWeight: 700, fontSize: '1rem', textTransform: 'none', borderRadius: '6px', boxShadow: '4px 4px 0 #1e1e1e', px: 4, py: 1.5, backgroundColor: '#fff', '&:hover': { backgroundColor: '#F5F5F5', boxShadow: '2px 2px 0 #1e1e1e', transform: 'translate(2px,2px)' }, transition: 'all 100ms' }}
              >
                For districts
              </MuiButton>
            </Box>

            <Typography sx={{ fontFamily: 'Lato, sans-serif', fontSize: '0.8rem', color: '#999' }}>
              Free for teachers &bull; No credit card &bull; FERPA compliant
            </Typography>

            {/* Use case labels */}
            <Box sx={{ mt: 5, display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontSize: '0.7rem', fontWeight: 700, color: '#BBB', textTransform: 'uppercase', letterSpacing: '0.08em', mb: 0.5 }}>
                Explore what Pressto can do →
              </Typography>
              {[
                { emoji: '🗞️', label: 'Publish a classroom newspaper' },
                { emoji: '✏️', label: 'Scaffold any writing assignment' },
                { emoji: '📋', label: 'Align prompts to your curriculum' },
                { emoji: '📖', label: 'Use RGR or Nearpod lessons' },
              ].map((item) => (
                <Box key={item.label} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography sx={{ fontSize: '0.85rem' }}>{item.emoji}</Typography>
                  <Typography sx={{ fontFamily: 'Lato, sans-serif', fontSize: '0.85rem', color: '#555' }}>{item.label}</Typography>
                </Box>
              ))}
            </Box>
          </Box>

          {/* Right: interactive desktop */}
          <Box sx={{ display: { xs: 'none', md: 'block' }, position: 'relative', height: 640 }}>
            {mounted && <Desktop />}
          </Box>
        </Box>
      </Box>

      {/* Bottom CTA */}
      <Box sx={{ backgroundColor: '#2C2C2C', borderTop: '2px solid #1e1e1e', py: { xs: 8, md: 10 }, px: { xs: 3, md: 6 }, textAlign: 'center' }}>
        <Box sx={{ maxWidth: 600, mx: 'auto' }}>
          <Typography variant="h2" sx={{ fontFamily: 'Montserrat, sans-serif', fontSize: { xs: '1.75rem', md: '2.25rem' }, fontWeight: 800, color: '#fff', mb: 2 }}>
            Give your students a real reason to write.
          </Typography>
          <Typography sx={{ fontFamily: 'Lato, sans-serif', fontSize: '1.05rem', color: 'rgba(255,255,255,0.7)', lineHeight: 1.7, mb: 5 }}>
            Free for teachers. Set up a classroom in under 10 minutes.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <MuiButton variant="contained" onClick={() => { track(Events.ClickedCTA, { location: 'bottom_v3', label: 'Get Started Free', destination: '/register' }); navigate('/register'); }} sx={{ backgroundColor: '#FFE082', color: '#2C2C2C', fontFamily: 'Montserrat, sans-serif', fontWeight: 700, fontSize: '1rem', textTransform: 'none', borderRadius: '6px', border: '2px solid #1e1e1e', boxShadow: '4px 4px 0 rgba(255,255,255,0.2)', px: 5, py: 1.5, '&:hover': { backgroundColor: '#FFD54F', transform: 'translate(2px,2px)', boxShadow: '2px 2px 0 rgba(255,255,255,0.2)' }, transition: 'all 100ms' }}>
              Get Started Free
            </MuiButton>
            <MuiButton variant="outlined" onClick={() => { track(Events.ClickedCTA, { location: 'bottom_v3', label: 'Contact us', destination: '/contact' }); navigate('/contact'); }} sx={{ borderColor: '#fff', border: '2px solid #fff', color: '#fff', fontFamily: 'Montserrat, sans-serif', fontWeight: 700, fontSize: '1rem', textTransform: 'none', borderRadius: '6px', boxShadow: '4px 4px 0 rgba(255,255,255,0.2)', px: 5, py: 1.5, '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)', transform: 'translate(2px,2px)' }, transition: 'all 100ms' }}>
              Contact us
            </MuiButton>
          </Box>
        </Box>
      </Box>
    </LandingLayout>
  );
}
